import type { InfiniteData } from '@tanstack/react-query'

import type {
  MonitorFeedListResponse,
  MonitorListResponse,
} from '@/hooks/monitors/fetchers'
import type { MonitorFeedListFilter } from '@/hooks/monitors/types'

import type { PaginateRequest } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type {
  MonitorFeedEntryDetailOut,
  MonitorFeedEntryOut,
  MonitorFeedEntryStatus,
  MonitorFeedStatusCounts,
  MonitorFeedTypeCount,
  MonitorOverviewOut,
} from '@/types/Monitor'

/**
 * Flatten an infinite feed's pages to a de-duped entry list. OFFSET paging over
 * a live, event_at-DESC feed reflows when new rows land at the top between
 * fetches, so the next page can repeat an already-shown row; de-dupe by id
 * (first/earlier page wins) to keep React keys unique and the user from seeing
 * the same entry twice on scroll. Shared by the per-monitor and org feeds.
 */
export const dedupeFeedEntries = (
  // Only the pages are read, so accept any InfiniteData page-param type.
  data: { pages: MonitorFeedListResponse[] } | undefined,
): MonitorFeedEntryOut[] => {
  const seen = new Set<UUID>()
  const flattened: MonitorFeedEntryOut[] = []
  for (const page of data?.pages ?? []) {
    for (const entry of page.data) {
      if (seen.has(entry.id)) continue
      seen.add(entry.id)
      flattened.push(entry)
    }
  }
  return flattened
}

// Pure cache-update helpers for the optimistic feed mutations. All of them use
// structural sharing: they return the same reference when nothing changed.

// Moving folders never touches the star (`is_bookmarked`) — that's orthogonal
// and owned by `withFeedEntryBookmarked`.
export const withFeedEntryStatus = <T extends MonitorFeedEntryOut>(
  entry: T,
  status: MonitorFeedEntryStatus,
): T => ({
  ...entry,
  status,
})

export const withFeedEntryBookmarked = <T extends MonitorFeedEntryOut>(
  entry: T,
  bookmarked: boolean,
): T => ({
  ...entry,
  is_bookmarked: bookmarked,
})

export const withFeedEntryRead = <T extends MonitorFeedEntryOut>(
  entry: T,
  read: boolean,
): T => ({
  ...entry,
  is_read: read,
})

type FeedListMembershipFilter = MonitorFeedListFilter & {
  bookmarked?: boolean
}

const feedEntryMatchesListFilter = (
  entry: MonitorFeedEntryOut,
  filter: FeedListMembershipFilter,
): boolean => {
  const statusMatches = filter.status
    ? entry.status === filter.status
    : entry.status !== 'trashed'
  const bookmarkMatches =
    filter.bookmarked === undefined || entry.is_bookmarked === filter.bookmarked
  return statusMatches && bookmarkMatches
}

const updateTypeCounts = (
  typeCounts: MonitorFeedTypeCount[],
  kind: string | null,
  delta: number,
): MonitorFeedTypeCount[] => {
  if (!kind || delta === 0) return typeCounts
  let found = false
  const next: MonitorFeedTypeCount[] = []
  for (const facet of typeCounts) {
    if (facet.kind !== kind) {
      next.push(facet)
      continue
    }
    found = true
    const count = Math.max(0, facet.count + delta)
    if (count > 0) next.push({ ...facet, count })
  }
  if (!found && delta > 0) next.push({ kind, count: delta })
  return next
}

// Only `inbox` and `archived` are tracked as folder counts; `trashed` is hidden
// and has no count. `all` and `unread` are non-trashed totals, so both move
// when an entry crosses the trashed boundary. The starred (`bookmarked`) count
// is orthogonal and owned by `updateBookmarkedCounts`.
const updateStatusCounts = (
  counts: MonitorFeedStatusCounts,
  previousStatus: MonitorFeedEntryStatus | undefined,
  nextStatus: MonitorFeedEntryStatus,
  isRead: boolean,
): MonitorFeedStatusCounts => {
  if (!previousStatus || previousStatus === nextStatus) return counts
  const next = { ...counts }
  if (previousStatus === 'inbox' || previousStatus === 'archived') {
    next[previousStatus] = Math.max(0, next[previousStatus] - 1)
  }
  if (nextStatus === 'inbox' || nextStatus === 'archived') {
    next[nextStatus] = next[nextStatus] + 1
  }
  const wasVisible = previousStatus !== 'trashed'
  const isVisible = nextStatus !== 'trashed'
  if (wasVisible && !isVisible) {
    next.all = Math.max(0, next.all - 1)
    if (!isRead) next.unread = Math.max(0, next.unread - 1)
  } else if (!wasVisible && isVisible) {
    next.all = next.all + 1
    if (!isRead) next.unread = next.unread + 1
  }
  return next
}

/**
 * Monitor-level unread delta caused by a folder move: trashing an unread entry
 * hides it from the unread tally; restoring it brings it back. Mirrors the
 * `unread` handling in `updateStatusCounts` for the monitor list/detail caches.
 */
export const statusChangeUnreadCountDelta = (
  entry: MonitorFeedEntryOut | undefined,
  nextStatus: MonitorFeedEntryStatus,
): number => {
  if (!entry || entry.is_read || entry.status === nextStatus) return 0
  const wasVisible = entry.status !== 'trashed'
  const isVisible = nextStatus !== 'trashed'
  if (wasVisible && !isVisible) return -1
  if (!wasVisible && isVisible) return 1
  return 0
}

const updateBookmarkedCounts = (
  counts: MonitorFeedStatusCounts,
  bookmarked: boolean,
): MonitorFeedStatusCounts => ({
  ...counts,
  bookmarked: bookmarked
    ? counts.bookmarked + 1
    : Math.max(0, counts.bookmarked - 1),
})

const updateUnreadCounts = (
  counts: MonitorFeedStatusCounts,
  read: boolean,
): MonitorFeedStatusCounts => {
  if (read && counts.unread <= 0) return counts
  return {
    ...counts,
    unread: read ? counts.unread - 1 : counts.unread + 1,
  }
}

export const findCachedFeedEntryReadState = (
  detail: MonitorFeedEntryOut | null | undefined,
  feedLists: Array<
    readonly [
      unknown,
      InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined,
    ]
  >,
  entryId: UUID,
): boolean | undefined => {
  if (detail?.id === entryId) return detail.is_read
  for (const [, data] of feedLists) {
    for (const page of data?.pages ?? []) {
      const entry = page.data.find((item) => item.id === entryId)
      if (entry) return entry.is_read
    }
  }
}

export const readStateUnreadCountDelta = (
  wasRead: boolean | undefined,
  nextRead: boolean,
): number => {
  if (wasRead === undefined || wasRead === nextRead) return 0
  return nextRead ? -1 : 1
}

// `unread_count` is live on list/get responses; it can still be undefined on
// cache entries built from POST/PATCH bodies (plain MonitorOut) — nothing to
// patch in that case.
export const updateMonitorUnreadCount = <T extends MonitorOverviewOut>(
  monitor: T | null | undefined,
  monitorId: UUID | '',
  delta: number,
): T | null | undefined => {
  if (
    !monitor ||
    monitor.id !== monitorId ||
    monitor.unread_count === undefined ||
    delta === 0
  ) {
    return monitor
  }
  return {
    ...monitor,
    unread_count: Math.max(0, monitor.unread_count + delta),
  }
}

export const updateMonitorListUnreadCount = (
  data: MonitorListResponse | undefined,
  monitorId: UUID | '',
  delta: number,
): MonitorListResponse | undefined => {
  if (!data || delta === 0) return data
  let changed = false
  const rows = data.data.map((monitor) => {
    if (monitor.id !== monitorId || monitor.unread_count === undefined) {
      return monitor
    }
    changed = true
    return {
      ...monitor,
      unread_count: Math.max(0, monitor.unread_count + delta),
    }
  })
  return changed ? { ...data, data: rows } : data
}

// Folder move: the matching row moves to `status` and is dropped from lists it
// no longer matches; once anything changed, every page's `status_counts`,
// `type_counts`, AND `count` copy is updated so all pages stay canonical.
// `count` is the dataset-wide total (identical on every page, like the other
// aggregates), so an eviction on the page that happened to hold the row must
// still propagate to every OTHER page's `count`, not just that one's.
export const updateFeedEntriesPageData = (
  data: InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined,
  entryId: UUID,
  status: MonitorFeedEntryStatus,
  previousStatus: MonitorFeedEntryStatus | undefined,
  filter: FeedListMembershipFilter,
): InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined => {
  if (!data) return data
  let changed = false
  // The moved row's read state feeds the unread count; only unread entries
  // crossing the trashed boundary move it.
  let movedEntryIsRead = true
  let typeCountKind: string | null = null
  let typeCountDelta = 0
  // Negative when the row is evicted from the list (no longer matches the
  // active filter); 0 when it just flips status in place.
  let countDelta = 0
  const pages = data.pages.map((page) => {
    let pageChanged = false
    const rows = page.data.flatMap((entry) => {
      // Same-status no-op: keep every reference intact.
      if (entry.id !== entryId || entry.status === status) return entry
      changed = true
      pageChanged = true
      movedEntryIsRead = entry.is_read
      const nextEntry = withFeedEntryStatus(entry, status)
      const stillMatches = feedEntryMatchesListFilter(nextEntry, filter)
      if (typeCountKind === null && entry.resource_kind) {
        typeCountKind = entry.resource_kind
        typeCountDelta =
          (stillMatches ? 1 : 0) -
          (feedEntryMatchesListFilter(entry, filter) ? 1 : 0)
      }
      if (!stillMatches) countDelta -= 1
      return stillMatches ? [nextEntry] : []
    })
    return pageChanged ? { ...page, data: rows } : page
  })
  if (!changed) return data
  return {
    ...data,
    pages: pages.map((page) => ({
      ...page,
      count: Math.max(0, page.count + countDelta),
      status_counts: updateStatusCounts(
        page.status_counts,
        previousStatus,
        status,
        movedEntryIsRead,
      ),
      type_counts: updateTypeCounts(
        page.type_counts,
        typeCountKind,
        typeCountDelta,
      ),
      // Same (status + bookmarked) scope as `count` and `feedEntryMatchesListFilter`
      // — kind-independent, so the page-total delta applies here unchanged too.
      type_counts_total: Math.max(0, page.type_counts_total + countDelta),
    })),
  }
}

// The star is orthogonal to folder status, so ordinary inbox/archived lists keep
// the row. Bookmarked-only lists drop it immediately when it no longer matches.
// `count` is the dataset-wide total (identical on every page), so an eviction
// must propagate to every page's `count`, not just the page that held the row —
// mirrors `updateFeedEntriesPageData`.
export const updateFeedEntriesBookmarkedPageData = (
  data: InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined,
  entryId: UUID,
  bookmarked: boolean,
  filter: FeedListMembershipFilter = {},
): InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined => {
  if (!data) return data
  let changed = false
  let typeCountKind: string | null = null
  let typeCountDelta = 0
  let countDelta = 0
  const pages = data.pages.map((page) => {
    let pageChanged = false
    const rows = page.data.flatMap((entry) => {
      if (entry.id !== entryId || entry.is_bookmarked === bookmarked) {
        return entry
      }
      changed = true
      pageChanged = true
      const nextEntry = withFeedEntryBookmarked(entry, bookmarked)
      const stillMatches = feedEntryMatchesListFilter(nextEntry, filter)
      if (typeCountKind === null && entry.resource_kind) {
        typeCountKind = entry.resource_kind
        typeCountDelta =
          (stillMatches ? 1 : 0) -
          (feedEntryMatchesListFilter(entry, filter) ? 1 : 0)
      }
      if (!stillMatches) countDelta -= 1
      return stillMatches ? [nextEntry] : []
    })
    return pageChanged ? { ...page, data: rows } : page
  })
  if (!changed) return data
  return {
    ...data,
    pages: pages.map((page) => ({
      ...page,
      count: Math.max(0, page.count + countDelta),
      status_counts: updateBookmarkedCounts(page.status_counts, bookmarked),
      type_counts: updateTypeCounts(
        page.type_counts,
        typeCountKind,
        typeCountDelta,
      ),
      // Same (status + bookmarked) scope as `count` — kind-independent, so the
      // page-total delta applies here unchanged too.
      type_counts_total: Math.max(0, page.type_counts_total + countDelta),
    })),
  }
}

// Read state is per-user and never changes list membership, so the row flips in
// place and every page's unread count moves.
export const updateFeedEntriesReadPageData = (
  data: InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined,
  entryId: UUID,
  read: boolean,
): InfiniteData<MonitorFeedListResponse, PaginateRequest> | undefined => {
  if (!data) return data
  let changed = false
  const pages = data.pages.map((page) => {
    let pageChanged = false
    const rows = page.data.map((entry) => {
      if (entry.id !== entryId || entry.is_read === read) return entry
      changed = true
      pageChanged = true
      return withFeedEntryRead(entry, read)
    })
    return pageChanged ? { ...page, data: rows } : page
  })
  if (!changed) return data
  return {
    ...data,
    pages: pages.map((page) => ({
      ...page,
      status_counts: updateUnreadCounts(page.status_counts, read),
    })),
  }
}

export const updateFeedEntryDetail = (
  entry: MonitorFeedEntryDetailOut | null | undefined,
  status: MonitorFeedEntryStatus,
): MonitorFeedEntryDetailOut | null | undefined =>
  entry ? withFeedEntryStatus(entry, status) : entry

export const updateFeedEntryDetailBookmarked = (
  entry: MonitorFeedEntryDetailOut | null | undefined,
  bookmarked: boolean,
): MonitorFeedEntryDetailOut | null | undefined =>
  entry ? withFeedEntryBookmarked(entry, bookmarked) : entry

export const updateFeedEntryDetailRead = (
  entry: MonitorFeedEntryDetailOut | null | undefined,
  read: boolean,
): MonitorFeedEntryDetailOut | null | undefined =>
  entry ? withFeedEntryRead(entry, read) : entry
